/**
 * Pure helpers for FileTranslationDialog: option lists and the enable/disable rules.
 * Kept out of the component so they stay testable and readable.
 */

import Constant from '@/common/constant/Constant';
import TranslationConstant from '@/common/constant/TranslationConstant';
import { getLanguageDisplayName } from '@/common/utils/translationUtils';
import {
    TRANSLATION_SOURCE_LANG_AUTO,
    TRANSLATION_MAX_TARGET_LANGS,
    isLangLockedByStatus,
    normalizeLangCode
} from '@/common/utils/translationJobUtils';

export const AUTO_DETECT_OPTION_CODE = TRANSLATION_SOURCE_LANG_AUTO;

/**
 * Dropdown options, derived from the same map the Lambda layer gates requests with - offering a
 * language that map does not have would mean the server silently rejects it.
 *
 * The alias filter is a safety net: the map holds only canonical codes, but an alias (he / tl) slipping
 * back in would show the same language twice.
 */
export const TRANSLATION_LANGUAGES = Object.keys(TranslationConstant.TRANSLATION_LANG_DISPLAY_JP)
    .filter(code => normalizeLangCode(code) === code)
    .map(code => ({ code, nameJp: getLanguageDisplayName(code) }));

/**
 * Source dropdown: auto-detect first and selected by default, then every supported language.
 */
export const buildSourceLangOptions = autoDetectLabel => [
    { code: AUTO_DETECT_OPTION_CODE, nameJp: autoDetectLabel },
    ...TRANSLATION_LANGUAGES
];

/**
 * Target dropdown, after applying:
 *  - the source language is hidden so it cannot be picked as a target (never when auto-detect,
 *    because the real language of the file is not known yet)
 *  - a language already translated or in progress is hidden; a failed one stays so it can be
 *    retried
 */
export const buildTargetLangOptions = (sourceLang, langStatusMap) =>
    TRANSLATION_LANGUAGES.filter(option => {
        if (sourceLang !== AUTO_DETECT_OPTION_CODE && option.code === sourceLang) return false;
        return !isLangLockedByStatus(langStatusMap?.[option.code]);
    });

/**
 * At the cap every unselected option is disabled, so the user can still deselect.
 */
export const isTargetOptionDisabled = (option, targetLangs) =>
    (targetLangs || []).length >= TRANSLATION_MAX_TARGET_LANGS && !(targetLangs || []).includes(option.code);

export const isMaxTargetLangsReached = targetLangs =>
    (targetLangs || []).length >= TRANSLATION_MAX_TARGET_LANGS;

export const canStartTranslate = (targetLangs, isStarting) =>
    (targetLangs || []).length > 0 && !isStarting;

/**
 * Changing the source language silently drops it from the selected targets - the spec asks for no
 * message, the language simply disappears.
 */
export const dropSourceFromTargets = (targetLangs, sourceLang) => {
    if (sourceLang === AUTO_DETECT_OPTION_CODE) return targetLangs || [];
    return (targetLangs || []).filter(lang => lang !== sourceLang);
};

export const getLanguageName = (code, autoDetectLabel) => {
    if (code === AUTO_DETECT_OPTION_CODE) return autoDetectLabel;
    return TRANSLATION_LANGUAGES.find(item => item.code === code)?.nameJp || code;
};

/**
 * The two halves of `rejected[]` need opposite handling, so they are separated once here.
 *
 * `unsupported` means the language list this dialog offers is ahead of what the server accepts -
 * nothing was started and only a message tells the user why. The rest means this client's view of
 * the file was stale (another tab or another user got there first), which is not an error: reloading
 * the status silently is the right answer.
 */
export const splitRejectedLangs = rejected => {
    const unsupported = [];
    const stale = [];

    (rejected || []).forEach(item => {
        if (!item?.lang) return;
        if (item.reason === Constant.TRANSLATION_REJECT_REASON.UNSUPPORTED_LANGUAGE) {
            unsupported.push(item.lang);
            return;
        }
        stale.push(item.lang);
    });

    return { unsupported, stale };
};

export const buildRejectedLangNames = (langs, autoDetectLabel) =>
    (langs || []).map(lang => getLanguageName(lang, autoDetectLabel)).join('、');
